import axios from 'axios';
import {
  clearTaskDetailMemoryCache,
  shouldInvalidateTaskDetailForMutation,
} from '../../services/taskServices/TaskDetailMemoryCache';

export const setupInterceptors = () => {
  // Axios Interceptor
  axios.interceptors.request.use((config) => {
    const impersonatedId = localStorage.getItem('impersonatedUserId');
    if (impersonatedId && config.headers) {
      const url = config.url || '';
      if (!url.includes('/chatHub') && !url.includes('/signalr') && !url.includes('/Authentication/login')) {
        config.headers['X-Impersonate-UserId'] = impersonatedId;
      }
    }
    return config;
  });

  // Axios Response Interceptor for 403 Handling
  axios.interceptors.response.use(
    (response) => {
      if (shouldInvalidateTaskDetailForMutation(response.config.method, response.config.url || '')) {
        clearTaskDetailMemoryCache();
      }
      return response;
    },
    (error) => {
      if (error.response?.status === 403 && localStorage.getItem('impersonatedUserId')) {
        const errorData = error.response?.data;
        const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message || errorData?.title || '';
        
        // Only revert if the 403 is specifically mapped to an invalid impersonation attempt
        if (errorMessage.toLowerCase().includes('impersonat') || errorMessage.toLowerCase().includes('owner')) {
          localStorage.removeItem('impersonatedUserId');
          window.location.reload();
        }
      }
      return Promise.reject(error);
    }
  );

  // Native Fetch Interceptor (Monkey-patching)
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const [resource, config] = args;
    const impersonatedId = localStorage.getItem('impersonatedUserId');

    let response: Response;
    if (impersonatedId) {
      const newConfig: RequestInit = config || {};
      const headers = new Headers(newConfig.headers || {});
      
      // Do not attach if it's a SignalR/WebSocket related request (Hubs)
      const url = typeof resource === 'string' ? resource : (resource as Request).url;
      if (!url.includes('/chatHub') && !url.includes('/signalr') && !url.includes('/Authentication/login')) {
        headers.set('X-Impersonate-UserId', impersonatedId);
      }
      
      newConfig.headers = headers;
      response = await originalFetch(resource, newConfig);
    } else {
      response = await originalFetch(...args);
    }

    if (response.status === 403 && localStorage.getItem('impersonatedUserId')) {
      try {
        const clonedResponse = response.clone();
        const errorData = await clonedResponse.json().catch(() => ({}));
        const errorMessage = errorData?.message || errorData?.title || '';
        
        if (errorMessage.toLowerCase().includes('impersonat') || errorMessage.toLowerCase().includes('owner')) {
          localStorage.removeItem('impersonatedUserId');
          window.location.reload();
        }
      } catch (e) {
        // Ignore JSON parsing errors
      }
    }

    const url = typeof resource === 'string' ? resource : (resource as Request).url;
    const method = config?.method ?? (typeof resource === 'string' ? 'GET' : (resource as Request).method);
    if (response.ok && shouldInvalidateTaskDetailForMutation(method, url)) {
      clearTaskDetailMemoryCache();
    }

    return response;
  };
};
